/**
 * useModels — AI 模型列表全局缓存
 *
 * 单例缓存,避免每个节点都去拉一次 `/api/v1/ai/models`。
 * 首次挂载拉取公开模型目录；空列表时每 10s 静默轮询，管理员启用模型后自动刷新。
 */
import { useEffect, useMemo, useState } from 'react'

import {
  aiApi,
  ApiError,
  type SilkAIModel,
  type SilkAITaskType,
} from '@/api'

export type { SilkAIModel } from '@/api'
import { readAccessToken, subscribeAuth } from '@/api/authSession'

interface CacheState {
  models: SilkAIModel[]
  loading: boolean
  error: string | null
  loadedAt: number | null
}

let cache: CacheState = {
  models: [],
  loading: false,
  error: null,
  loadedAt: null,
}

const listeners = new Set<() => void>()

function notify() {
  for (const l of listeners) l()
}

function normalizeModels(raw: unknown): SilkAIModel[] {
  if (!Array.isArray(raw)) return []
  return raw.filter((m): m is SilkAIModel => !!m && typeof m === 'object' && typeof m.id === 'string')
}

/** 与 backend 对齐：专用 image/video/audio SKU 不可跨类型；多模态 LLM 可出现在多种节点 */
export function isMultimodalModel(model: SilkAIModel | string): boolean {
  const id = (typeof model === 'string' ? model : model.id).toLowerCase()
  if (
    id.includes('gemini') ||
    id.includes('gpt-4') ||
    id.includes('gpt-4o') ||
    id.includes('qwen-vl') ||
    id.includes('qwen2-vl') ||
    id.includes('vision')
  ) {
    return true
  }
  if (id.includes('claude') && id.includes('vision')) return true
  const caps = typeof model === 'string' ? [] : (model.capabilities ?? [])
  const primary = typeof model === 'string' ? '' : String(model.type ?? '').trim().toLowerCase()
  if (primary === 'text' && caps.includes('image')) return true
  return false
}

/** 与 backend SanitizeModelCapabilities / ModelSupportsTaskType 对齐 */
export function modelMatchesTaskType(model: SilkAIModel, type: SilkAITaskType) {
  const primary = String(model.type ?? '').trim().toLowerCase()
  if (primary === type) return true
  const caps = (model.capabilities ?? []).some((c) => String(c).trim().toLowerCase() === type)
  if (!caps) return false
  return isMultimodalModel(model)
}

/** 从已过滤列表或缓存中解析当前节点可用模型（拒绝跨类型缓存） */
export function resolveModelForTask(
  modelId: string | undefined,
  candidates: SilkAIModel[],
  taskType: SilkAITaskType,
): SilkAIModel | undefined {
  if (!modelId) return undefined
  const hit = candidates.find((m) => m.id === modelId)
  if (hit) return hit
  const cached = lookupCachedModel(modelId)
  if (cached && modelMatchesTaskType(cached, taskType)) return cached
  return undefined
}

/** 从未按 type 过滤的全量缓存里解析模型（节点已存 modelId 时展示用） */
export function lookupCachedModel(modelId: string | undefined): SilkAIModel | undefined {
  if (!modelId) return undefined
  return cache.models.find((m) => m.id === modelId)
}

const CACHE_TTL_MS = 60_000
const POLL_INTERVAL_MS = 10_000

let pollTimer: ReturnType<typeof setInterval> | null = null
let subscriberCount = 0

function startModelPolling() {
  if (pollTimer) return
  pollTimer = setInterval(() => {
    void fetchOnce(true, true)
  }, POLL_INTERVAL_MS)
}

function stopModelPolling() {
  if (!pollTimer) return
  clearInterval(pollTimer)
  pollTimer = null
}

/** 工作台挂载时可主动拉取模型列表 */
export function prefetchModels(force = false) {
  void fetchOnce(force)
}

/** 顶栏手动刷新：强制拉取模型目录并显示 loading */
export function refreshModelsCatalog() {
  void fetchOnceManual()
}

async function fetchOnceManual() {
  if (cache.loading) return
  cache = { ...cache, loading: true, error: null }
  notify()
  try {
    const models = normalizeModels(await aiApi.models())
    cache = { models, loading: false, error: null, loadedAt: Date.now() }
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : (err as Error).message
    cache = { ...cache, loading: false, error: msg, loadedAt: Date.now() }
  }
  notify()
}

/** 登录/登出后清空缓存并重新拉取 */
export function resetModelsCache() {
  cache = { models: [], loading: false, error: null, loadedAt: null }
  notify()
}

function cacheStillFresh(force: boolean): boolean {
  if (force || !cache.loadedAt) return false
  return Date.now() - cache.loadedAt < CACHE_TTL_MS
}

async function fetchOnce(force = false, silent = false) {
  if (cache.loading) return
  if (!force && cacheStillFresh(false)) return

  if (force && silent) {
    try {
      const models = normalizeModels(await aiApi.models())
      cache = { models, loading: false, error: null, loadedAt: Date.now() }
    } catch (err) {
      if (!cache.loadedAt) return
      if (cache.models.length > 0) return
      const msg = err instanceof ApiError ? err.message : (err as Error).message
      cache = { ...cache, loading: false, error: msg, loadedAt: Date.now() }
    }
    notify()
    return
  }

  if (force && cacheStillFresh(true)) return

  const isInitial = !cache.loadedAt
  if (isInitial) {
    cache = { ...cache, loading: true, error: null }
    notify()
  }
  try {
    const models = normalizeModels(await aiApi.models())
    cache = { models, loading: false, error: null, loadedAt: Date.now() }
  } catch (err) {
    const msg = err instanceof ApiError ? err.message : (err as Error).message
    cache = { ...cache, loading: false, error: msg, loadedAt: Date.now() }
  }
  notify()
}

/**
 * 订阅模型列表。可选按 type 过滤。
 *
 * 首次任意调用方挂载时会触发一次拉取；有订阅方时每 10s 后台刷新（不闪「加载中」）。
 */
export function useModels(type?: SilkAITaskType) {
  const [snap, setSnap] = useState(cache)

  useEffect(() => {
    subscriberCount += 1
    startModelPolling()

    const sync = () => setSnap({ ...cache })
    const listener = () => {
      setSnap((prev) => {
        if (
          prev.models === cache.models &&
          prev.loading === cache.loading &&
          prev.error === cache.error &&
          prev.loadedAt === cache.loadedAt
        ) {
          return prev
        }
        return { ...cache }
      })
    }
    listeners.add(listener)
    sync()
    void fetchOnce(!cache.loadedAt || !!cache.error)
    return () => {
      listeners.delete(listener)
      subscriberCount -= 1
      if (subscriberCount <= 0) stopModelPolling()
    }
  }, [])

  useEffect(() => {
    let hadToken = !!readAccessToken()
    const unsub = subscribeAuth(() => {
      const hasToken = !!readAccessToken()
      if (hasToken === hadToken) return
      hadToken = hasToken
      resetModelsCache()
      void fetchOnce(true)
    })
    return unsub
  }, [])

  const filtered = useMemo(
    () =>
      type
        ? snap.models.filter((m) => modelMatchesTaskType(m, type))
        : snap.models,
    [snap.models, type],
  )

  const availability = useMemo(
    () => resolveModelAvailability(snap.loading, snap.error, snap.models.length),
    [snap.loading, snap.error, snap.models.length],
  )

  return {
    models: filtered,
    allModels: snap.models,
    loading: snap.loading,
    error: snap.error,
    availability,
    refresh: () => void fetchOnce(true, !!cache.loadedAt),
  }
}

export type ModelAvailabilityStatus = 'loading' | 'ready' | 'empty' | 'error'

export type ModelAvailability = {
  status: ModelAvailabilityStatus
  error: string | null
}

export function resolveModelAvailability(
  loading: boolean,
  error: string | null,
  count: number,
): ModelAvailability {
  if (loading) return { status: 'loading', error: null }
  if (error) return { status: 'error', error }
  if (count === 0) return { status: 'empty', error: null }
  return { status: 'ready', error: null }
}

/** 全量模型目录可用性（不按 type 过滤） */
export function useModelAvailability(): ModelAvailability {
  const { availability } = useModels()
  return availability
}
